import { Box } from "@mui/material";
import { useFormattedContent } from "@/hooks";

/**
 * RichTextDisplay - A reusable component for safely rendering HTML content from rich text editors
 * 
 * This component handles:
 * - Sanitizing white backgrounds from rich text editors (converts rgb(255,255,255) to transparent)
 * - Applying consistent styling to HTML elements
 * - Safe rendering of HTML content
 * - Automatically hides if content is empty (only HTML tags/whitespace)
 * 
 * @param {Object} props
 * @param {string} props.content - The HTML content to render
 * @param {Object} props.sx - Additional Material-UI sx styles
 * @param {boolean} props.userSelect - Whether to allow text selection (default: true)
 */
const RichTextDisplay = ({ 
  content, 
  sx = {}, 
  userSelect = true,
  ...otherProps 
}) => {
  // Use the hook to format content and check if it should be displayed
  const { formattedContent, hasContent } = useFormattedContent(content);

  // Don't render if there's no meaningful content
  if (!hasContent) return null;

  return (
    <Box
      dangerouslySetInnerHTML={{ __html: formattedContent }}
      sx={{
        // Base styles for rendered HTML content
        "& p": { 
          mb: 1,
          lineHeight: 1.6,
        },
        "& ul, & ol": { 
          pl: 2,
          mb: 1,
        },
        "& li": {
          mb: 0.5,
        },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          mt: 2,
          mb: 1,
          fontWeight: 600,
        },
        "& img": {
          maxWidth: "100%",
          height: "auto",
          borderRadius: 1,
        },
        "& pre": {
          bgcolor: "grey.100",
          p: 2,
          borderRadius: 1,
          overflow: "auto",
        },
        "& code": {
          bgcolor: "grey.100",
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          fontSize: "0.875em",
        },
        "& blockquote": {
          borderLeft: 3,
          borderColor: "primary.main",
          pl: 2,
          ml: 0,
          fontStyle: "italic",
          color: "text.secondary",
        },
        "& table": {
          width: "100%",
          borderCollapse: "collapse",
          mb: 2,
        },
        "& th, & td": {
          border: 1,
          borderColor: "divider",
          p: 1,
          textAlign: "left",
        },
        "& th": {
          bgcolor: "grey.100",
          fontWeight: 600,
        },
        "& a": {
          color: "primary.main",
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },
        "& iframe": {
          maxWidth: "100%",
          border: "none",
          borderRadius: 1,
        },
        userSelect: userSelect ? "text" : "none",
        // Allow custom styles to override defaults
        ...sx,
      }}
      {...otherProps}
    />
  );
};

export default RichTextDisplay;
